/**
 * Step 6: signed in, verified, on their own.
 *
 * The reader's books, by status, with their progress and quiz result, read
 * from the shelf (a book is marked on its own page). And sign-out and deletion,
 * because both are commitments rather than features: sign-out has to end the
 * session on the server, and deletion has to actually delete.
 */

import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router'
import { NICKNAME_MAX_LENGTH, deleteAccountInputSchema, nicknameSchema } from '../../shared/auth-schemas.ts'
import type { MessageKey } from '../../shared/message-keys.ts'
import { AccountReviews } from '../components/AccountReviews.tsx'
import { AccountShelves } from '../components/AccountShelves.tsx'
import { Alert } from '../components/Alert.tsx'
import { Field } from '../components/Field.tsx'
import { useI18n } from '../i18n/index.tsx'
import { messageKeyFor } from '../lib/api.ts'
import { authClient } from '../lib/auth-client.ts'

export function Account() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { data: session, isPending } = authClient.useSession()

  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState<MessageKey | null>(null)
  const [busy, setBusy] = useState(false)
  const [deleted, setDeleted] = useState(false)

  if (isPending) return <p>{t('app.loading')}</p>
  if (deleted) {
    return (
      <section className="panel">
        <h1>{t('account.delete.done')}</h1>
      </section>
    )
  }
  if (!session) return <Navigate to="/signin" replace />
  const accountEmail = session.user.email

  async function signOut() {
    // Better Auth deletes the session row, not just the cookie.
    await authClient.signOut()
    await navigate('/signin')
  }

  async function onDelete(event: FormEvent) {
    event.preventDefault()
    setError(null)

    // The reader types their own address: the same in every language, and
    // proof they mean this account, not a word to be copied from the label.
    if (confirmation.trim().toLowerCase() !== accountEmail.trim().toLowerCase()) {
      setError('error.confirmation_mismatch')
      return
    }

    const parsed = deleteAccountInputSchema.safeParse({ password })
    if (!parsed.success) {
      setError('error.password_required')
      return
    }

    setBusy(true)
    const { error: failure } = await authClient.deleteUser({ password: parsed.data.password })
    setBusy(false)

    if (failure) {
      setError(messageKeyFor(failure))
      return
    }
    setDeleted(true)
  }

  return (
    <section className="panel">
      <h1>{t('account.title')}</h1>

      <p>
        {t('account.signed_in_as')} <strong>{session.user.email}</strong>
      </p>
      {session.user.emailVerified ? <p>{t('account.verified')}</p> : null}

      <button type="button" className="button" onClick={() => void signOut()}>
        {t('account.signout')}
      </button>

      <NicknameForm current={session.user.name} />

      <hr />

      <AccountShelves />

      <hr />

      <AccountReviews />

      <hr />

      {/* Collapsed until asked for: the page is about the reader's books, and
          deleting the account is a decision, not something to scroll past. */}
      <details className="danger-zone">
        <summary>
          <h2>{t('account.delete.title')}</h2>
        </summary>
        <p>{t('account.delete.warning')}</p>

        <form onSubmit={(event) => void onDelete(event)} noValidate>
          <Field
            label={t('signin.password.label')}
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => {
              setPassword(event.target.value)
            }}
          />

          <Field
            label={t('account.delete.confirm_label')}
            type="email"
            name="confirmation"
            autoComplete="off"
            required
            value={confirmation}
            onChange={(event) => {
              setConfirmation(event.target.value)
            }}
          />

          {error ? <Alert tone="error">{t(error)}</Alert> : null}

          <button type="submit" className="button button--danger" disabled={busy}>
            {t('account.delete.submit')}
          </button>
        </form>
      </details>
    </section>
  )
}

/**
 * The optional nickname, changed here. Empty removes it. Shown to the reader
 * in the header and beside any review they write; the privacy page says so.
 */
function NicknameForm({ current }: { current: string }) {
  const { t } = useI18n()
  const { refetch } = authClient.useSession()
  const [nickname, setNickname] = useState(current)
  const [error, setError] = useState<MessageKey | null>(null)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setSaved(false)
    const parsed = nicknameSchema.safeParse(nickname)
    if (!parsed.success) {
      setError((parsed.error.issues[0]?.message ?? 'error.nickname_invalid') as MessageKey)
      return
    }
    setBusy(true)
    const { error: failure } = await authClient.updateUser({ name: parsed.data })
    setBusy(false)
    if (failure) {
      setError(messageKeyFor(failure))
      return
    }
    await refetch()
    setNickname(parsed.data)
    setSaved(true)
  }

  return (
    <form className="account-nickname" onSubmit={(event) => void onSubmit(event)} noValidate>
      <Field
        label={t('register.nickname.label')}
        hint={t('register.nickname.hint')}
        type="text"
        name="nickname"
        autoComplete="nickname"
        maxLength={NICKNAME_MAX_LENGTH}
        value={nickname}
        onChange={(event) => {
          setNickname(event.target.value)
          setSaved(false)
        }}
      />
      {error ? <Alert tone="error">{t(error)}</Alert> : null}
      <p className="account-nickname__actions">
        <button type="submit" className="button button--quiet" disabled={busy || nickname.trim() === current.trim()}>
          {t('account.nickname.save')}
        </button>
        <span role="status">{saved ? t('shelf.saved') : ''}</span>
      </p>
    </form>
  )
}
